import * as yup from 'yup';

const MAX_PROPERTIES = 6;

type PropertyFields = {
  [key: string]: yup.Schema<any>;
};

const propertyFields: PropertyFields = {};

function requiredIfMarried<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['maritalStatus '], {
    is: (maritalStatus: string) => maritalStatus !== 'single',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['citizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCondition<T extends yup.AnySchema>(
  schemaType: T,
  fields: string[],
  condition: (...values: any[]) => boolean,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(fields, {
    is: (...fieldValues: any[]) => condition(...fieldValues),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

type OverseasPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const overseasPropertyFields: OverseasPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  overseasPropertyFields[`overseasPropertyLocation${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please enter property location ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });
  overseasPropertyFields[`overseasPropertyValue${i}`] = yup
    .number()
    .typeError(`Property value ${i} must be a number`)
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please enter property value ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyOwner${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please select owner ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyUse${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please select property usage ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`isMortgageOnOverseasProperty${i}`] = yup
    .boolean()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required('Please confirm your mortgage'),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageLoanLimit${i}`] = yup
    .number()
    .typeError(`Loan limit ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Please enter loan limit ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageInterest${i}`] = yup
    .number()
    .max(100, 'Interest rate cannot exceed 100')
    .typeError(`Interest rate ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Interest rate is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageLoanTerm${i}`] = yup
    .number()
    .typeError(`Loan term ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Loan term is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageRepayment${i}`] = yup
    .number()
    .typeError(`Repayment sum ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) =>
        schema.required(`Repayment on overseas mortgage is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyRent${i}`] = yup
    .number()
    .when(`overseasPropertyUse${i}`, {
      is: (value: string) =>
        value === 'rented' ||
        value === 'short_term_lease' ||
        value === 'investment_property',
      then: (schema) => schema.required(`Please enter rent ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyRentFrequency${i}`] = yup
    .string()
    .when(`overseasPropertyUse${i}`, {
      is: (value: string) =>
        value === 'rented' ||
        value === 'short_term_lease' ||
        value === 'investment_property',
      then: (schema) => schema.required(`Please enter rent frequency ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });
}

export const primaryDetailSchema = yup.object().shape({
  purchasingUnder: yup.string().required('Please select any option.'),
  propertyType: yup.string().required('Please select any option.'),
  coapplicantFirstName: yup
    .string()
    .required('Please enter coapplicant first name'),
  coapplicantLastName: yup
    .string()
    .required('Please enter coapplicant last name'),
  coapplicantEmail: yup.string().required('Please enter coapplicant email'),
  coapplicantPhoneNumber: yup
    .string()
    .required('Please enter coapplicant phone number'),
  coapplicantCountry: yup.string().required('Please select any option.'),
  coapplicantPermanentResident: yup.boolean(),
  coapplicantRelationship: yup.string().required('Please select any option.'),
  coapplicantCitizenship: yup.string().required('Please select any option.'),
  coapplicantLivingTogether: yup
    .boolean()
    .required('Please select any option.'),
  coapplicantCurrentResidentialStatus: yup
    .string()
    .required('Please select any option.'),
  coapplicantRentExpense: yup.number().required('Please enter your rent'),
  coapplicantFrequencyOfRent: yup
    .string()
    .required('Please select your frequency of rent'),
  coapplicantIsTheResidentialPlaceMortgaged: yup
    .boolean()
    .required('Please select any option.'),

  citizenship: yup.string().required('Please select any option.'),
  visaSubClass: requiredIfPR(
    yup.string(),
    'Visa sub class is required.',
  ).nullable(),
  visaExpiryDate: requiredIfPR(yup.string(), 'Visa expire date is required.'),
  country: yup.string().required('Please select any option.'),
  permanentResident: yup.boolean().required('Please select any option.'),
  currentResidentalStatus: yup.string().required('Please select any option.'),
  residentalOtherSpecification: yup.string().when(['residentialStatus '], {
    is: (residentialStatus: string) => residentialStatus === 'other',
    then: (schema) => schema.required('Please provide more details'),
    otherwise: (schema) => schema.notRequired(),
  }),
  maritalStatus: yup.string().required('Please select any option.').nullable(),
  dependentChildren: requiredIfMarried(
    yup.boolean(),
    'Please select option.',
  ).nullable(),
  numberOfDependents: requiredIfCondition(
    yup.number(),
    ['dependentChildren'],
    (dependentChildren: boolean) => dependentChildren === true,
    'Number of dependent children is required',
  ),
  rentExpense: requiredIfCondition(
    yup.number(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please enter your rent',
  ),
  frequencyOfRent: requiredIfCondition(
    yup.string(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please select your frequency of rent',
  ),
  isTheResidentialPlaceMortgaged: requiredIfCondition(
    yup.boolean(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'live_own_place',
    'Please select any option.',
  ),

  propertyIds: yup.array(),
  overseasPropertyIds: yup.array(),
  maxPurchaseAmount: yup.number().required('Please enter your amount'),
  propertyLocation: yup.string().required('Please select property location'),
  maxDepositDownpayment: yup
    .number()
    .required('Please enter your maximum deposit down payment amount'),

  useExistingAusProperty: yup.string().typeError('Please select any option.'),
});

export const personalDetailLabelMapper = {
  citizenship: 'What’s your citizenship?',
  country: 'Which country do you currently reside in?',
  permanentResident: 'Are you a Permanent Resident(PR) of this country?',
  currentResidentalStatus: 'What’s your current residential status?',
  maritalStatus: 'What is your marital status?',
  dependentChildren: 'Do you have any dependent children?',
};
